/**
 * Vuex module for the bulk-upload of member imports
 * @author      Jeff Adams <jeff@tinyfly.com>
 * @copyright   Traxo, Inc. 2020
 */

import { MemberImportApi } from '@/services/api-member-import.js';

// Local state -----------------------------------------------------
export const state = getDefaultState();

// Computed properties of the state --------------------------------
export const getters = {
  counts: (state) => {
    let counts = {};
    if (state.current.results && state.current.results.length) {
      state.current.results.forEach((result) => {
        let key = result.label.replace(/\s/gi, '_').toLowerCase();
        counts[key] = result.count;
      });
    }

    return counts;
  },
};

// Synchronous mutation of the local state -------------------------
export const mutations = {
  UPDATE_IMPORT(state, importData) {
    state.current = { ...importData };
  },

  UPDATE_POLL_TIMER(state, { timerId, interval }) {
    state.poll = {
      timer: timerId,
      isActive: true,
      elapsedSeconds: state.poll.elapsedSeconds + interval,
    };
  },

  CLEAR_POLL_TIMER(state) {
    clearTimeout(state.poll.timer);
    state.poll = {
      timer: null,
      isActive: false,
      elapsedSeconds: 0,
    };
  },

  RESET_STATE(state, defaultState) {
    state.current = defaultState.current;
    state.poll = defaultState.poll;
  },
};

// Async CRUD actions against the api ------------------------------
export const actions = {
  /**
   * Fetch a list of all imports
   * @param {Object} context - vuex context object
   * @returns {Promise}
   */
  index({ commit }) {
    return MemberImportApi.index().then((importItems = []) => {
      let latest = {};

      // We only care if the latest import is still in process
      // in some way.
      if (importItems.length && importItems[0].status !== 'Processed' && importItems[0].status !== 'Failed') {
        latest = importItems[0];
      }
      commit('UPDATE_IMPORT', latest);
    });
  },

  /**
   * Create a new import preview
   * @param {Object} context - vuex context object
   * @param {Object} payload
   * @param {Object} payload.data - form payload includes a file
   * @param {Function} payload.progressFn - callback function for handling upload progress updates
   * @returns {Promise}
   */
  store({ commit }, { data, progressFn }) {
    return MemberImportApi.store(data, progressFn).then((importItem) => {
      commit('UPDATE_IMPORT', importItem);
    });
  },

  /**
   * Process an import preview
   * @param {Object} context - vuex context object
   * @param {Object} payload
   * @param {string} payload.id - import id
   * @returns {Promise}
   */
  confirm({ commit }, { id }) {
    return MemberImportApi.confirm(id).then((importItem) => {
      commit('UPDATE_IMPORT', importItem);
    });
  },

  /**
   * Fetch the details of a specific import
   * @param {Object} context - vuex context object
   * @param {Object} payload
   * @param {string} payload.id - import id
   * @returns {Promise}
   */
  show({ commit, dispatch }, payload) {
    return MemberImportApi.show(payload.id)
      .then((importItem) => {
        commit('UPDATE_IMPORT', importItem);
        dispatch('startPolling', payload);
      })
      .catch((err) => {
        dispatch('stopPolling');
        throw err;
      });
  },

  /**
   * Re-process an import with a new file
   * @param {Object} context - vuex context object
   * @param {Object} payload
   * @param {Object} payload.data - form payload includes a file
   * @param {Function} payload.progressFn - callback function for handling upload progress updates
   * @returns {Promise}
   */
  update({ commit }, { id, data, progressFn }) {
    return MemberImportApi.update(id, data, progressFn).then((importItem) => {
      commit('UPDATE_IMPORT', importItem);
    });
  },

  /**
   * Starts polling the server for a non-pending response
   * @param {Object} context - vuex context object
   * @param {Object} payload
   * @param {string} payload.id - Import id
   * @param {number} [payload.interval=5000] - How often should the poll happen, in milliseconds
   * @param {number} [payload.expires=5000] - For how long should the polling run, in milliseconds. 0 will never stop
   */
  startPolling({ commit, dispatch, state }, { id, interval = 2500, expires = 5000 }) {
    // if the status of the current import is in one of the pending states go
    // ahead and poll the server for a specific amount of time.
    let isPending = state.current.status === 'Queued' || state.current.status === 'Processing';
    let continuePolling = expires === 0 || state.poll.elapsedSeconds < expires;

    return new Promise((resolve, reject) => {
      if (isPending && continuePolling) {
        let timerId = setTimeout(function () {
          dispatch('show', { id, interval, expires })
            .then((response) => resolve(response))
            .catch((err) => reject(err));
        }, interval);

        commit('UPDATE_POLL_TIMER', { timerId, interval });
      } else {
        dispatch('stopPolling').then((response) => resolve(response));
      }
    });
  },

  /**
   * Stops any existing polling timers
   * @param {Object} context - vuex context object
   */
  stopPolling({ commit, state }) {
    return new Promise((resolve) => {
      if (state.poll.timer) {
        commit('CLEAR_POLL_TIMER');
      }

      resolve();
    });
  },

  /**
   * Sets the state back to the default values
   * @returns {Promise}
   */
  reset({ commit }) {
    commit('RESET_STATE', getDefaultState());
  },
};

/// ///

function getDefaultState() {
  return {
    current: {},
    poll: {
      elapsedSeconds: 0,
      timer: null,
      isActive: false,
    },
  };
}
